import { Conclusion } from "./../dtos/communication.dto";
import { useMutation, useQuery } from "@tanstack/react-query";
import { api } from "api";
import { T_Api_Error_Res, T_Api_Success_Res } from "./api.types";
import {
  CommunicationDto,
  CommunicationSchemaDto,
  MessageType,
  ModelOption,
  NegotiationDto,
} from "dtos/communication.dto";
import { AxiosError } from "axios";
import { negotiationInitialValue } from "pages/LoanIntelligence/Calculator.utils";
import { NegotiationChatDto } from "dtos/negotiation.chat.dto";

export const communicationService = {
  useGetCommunicationConfigByCreditorId: (creditorId: string | undefined) => {
    return useQuery({
      queryKey: ["communication", "config", creditorId],
      queryFn: async () => {
        try {
          const res = await api.get(`/communications/${creditorId}`);
          return res.data as T_Api_Success_Res<Omit<CommunicationSchemaDto, "emails">>;
        } catch (error) {
          if (error instanceof AxiosError) {
            const err = error as AxiosError<T_Api_Error_Res>;
            if (
              err.response?.status === 404 &&
              err?.response?.data?.message === "No communication payload found for this creditor id."
            ) {
              return { result: { ...negotiationInitialValue, emails: [] } };
            } else {
              throw error;
            }
          } else {
            throw error;
          }
        }
      },
      enabled: !!creditorId,
    });
  },
  useGetCommunicationChatsByCreditorId: (creditorId: string | undefined, shouldRefetch: boolean) => {
    return useQuery({
      queryKey: ["coomunication", "chats", creditorId],
      queryFn: async () => {
        const res = await api.get(`/communications/chats/${creditorId}`);
        return res.data as T_Api_Success_Res<
          (Pick<CommunicationSchemaDto, "emails">["emails"][number] & {
            parent_reply_id?: string;
            reply_id?: string;
            mail_thread_id?: string;
            mail_message_id?: string;
            type: MessageType;
            versions: NegotiationChatDto[];
          })[]
        >;
      },
      enabled: !!creditorId,
      refetchInterval: shouldRefetch === true ? 10000 : false,
    });
  },
  usePostCommunication: () => {
    return useMutation({
      mutationKey: ["communication", "post"],
      mutationFn: async (body: Pick<CommunicationDto, "creditor_id" | "config" | "context">) => {
        const res = await api.post(`/communications/create`, body);
        return res.data as T_Api_Success_Res<CommunicationDto>;
      },
    });
  },
  useInitiateNegotiation: () => {
    return useMutation({
      mutationKey: ["negotiations", "initial-mail"],
      mutationFn: async (body: { app_key: string; creditor_id: string }) => {
        const res = await api.post(`/negotiations/initial-mail`, body);
        return res.data as T_Api_Success_Res<CommunicationDto>;
      },
    });
  },
  useSaveNegotation: () => {
    return useMutation({
      mutationKey: ["negotiations", "save-mail"],
      mutationFn: async (body: { subject: string; body: string; id: string }) => {
        console.log({ body });
        const res = await api.patch(`/communications/edit-message/${body.id}`, {
          body: body.body,
          subject: body.subject,
        });
        return res.data as T_Api_Success_Res<CommunicationDto>;
      },
    });
  },

  useFollowUpNegotiation: () => {
    return useMutation({
      mutationKey: ["negotiations", "followup-mail "],
      mutationFn: async (body: { app_key: string; creditor_id: string; creditor_response: string }) => {
        const res = await api.post(`/negotiations/followup-mail `, body);
        return res.data as T_Api_Success_Res<CommunicationDto>;
      },
    });
  },

  useReGenerateNegotiation: () => {
    return useMutation({
      mutationKey: ["negotiations", "regenerate-mail"],
      mutationFn: async (body: {
        app_key: string;
        creditor_id: string;
        mail: string;
        _id: string;
        user_input?: string;
      }) => {
        const res = await api.post(`/negotiations/regenerate-mail`, body);
        return res.data as T_Api_Success_Res<any>;
      },
    });
  },

  useRegenerateNegotiationTonality: () => {
    return useMutation({
      mutationKey: ["negotiations", "regenerate-mail-tonality"],
      mutationFn: async (body: { app_key: string; creditor_id: string; mail: string; _id: string }) => {
        const res = await api.post(`/negotiations/tonality/regenerate`, body);
        return res.data as T_Api_Success_Res<any>;
      },
    });
  },

  useGetHardShipFlag: (appkey: string) => {
    return useQuery({
      queryKey: ["negotiations", "/flagship", appkey],
      queryFn: async () => {
        const res = await api.get(`/communications/hardship-flags/${appkey}`);
        return res.data as T_Api_Success_Res<string[]>;
      },
    });
  },
  useUpdateHardShipFlag: () => {
    return useMutation({
      mutationKey: ["updateHardshipflag"],
      mutationFn: async (body: { appkey: string; data: string[] }) => {
        const res = await api.patch(`/communications/update-hardship-flags/${body.appkey}`, {
          hardship_flags: body.data,
        });
        return res.data as T_Api_Success_Res<string[]>;
      },
    });
  },

  useGetTonalityList: () => {
    return useQuery({
      queryKey: ["negotiations", "/values/tonality"],
      queryFn: async () => {
        const res = await api.get(`/negotiations/values/tonality`);
        return res.data as T_Api_Success_Res<
          Record<
            string,
            {
              empathy: string;
              rigidity: string;
              system_agression: number;
              formality: string;
              urgency_level: string;
              persuasion_mode: string;
              emotion_focus: string[];
              sentence_style: string;
              politeness: string;
            }
          >
        >;
      },
    });
  },

  useGetChatById: (id: string | undefined) => {
    return useQuery({
      queryKey: ["negotiations", "chat", id],
      async queryFn() {
        const res = await api.get(`/negotiations/${id}`);
        return res.data as T_Api_Success_Res<NegotiationChatDto>;
      },
      enabled: Boolean(id?.length),
    });
  },
  useDeleteChat: () => {
    return useMutation({
      mutationKey: ["clear-chat"],
      mutationFn: async ({ chatId }: { chatId: string }) => {
        const res = await api.delete(`/communications/clear-chat/${chatId}`);
        return res.data;
      },
    });
  },
  useGenerateConclusion: () => {
    return useMutation({
      mutationKey: ["generate-chat-summary"],
      mutationFn: async ({ app_key, creditor_id }: { creditor_id?: string; app_key?: string }) => {
        const res = await api.post(`/negotiations/generate-conclusion`, { app_key, creditor_id });
        return res.data;
      },
    });
  },
};
